import { useState } from "react";
import { BadgeCheck, Bike, Clock, Info, TriangleAlert } from "lucide-react";
import { useTranslate } from "@/lib/i18n";
import type { InvoiceChannel, InvoiceStatus } from "@/lib/api/types";
import { CommandIds } from "@/modules/pos/lib/assignDrivers";
import { dispatchActions, dispatchChip, formatCountdown } from "@/modules/pos/lib/dispatchBoard";
import { useDispatchAccess, useDispatchBoard } from "@/modules/pos/lib/useDispatchBoard";
import { DispatchProblemSheet } from "../dispatch/DispatchProblemSheet";
import { dispatchChipTone, useDispatchChipText, type Tone } from "../dispatch/DispatchStatusChip";

const BADGE_TONE: Record<Tone, string> = {
  info: "border-[color:var(--info)] bg-[color:var(--info-100)] text-[color:var(--info)]",
  success: "border-[color:var(--success)] bg-[color:var(--success-100)] text-[color:var(--success)]",
  warning: "border-[color:var(--warning)] bg-[color:var(--warning-100,var(--surface-alt))] text-[color:var(--warning)]",
  danger: "border-[color:var(--danger)] bg-[color:var(--danger-100)] text-[color:var(--danger)]",
};
import { AssignDriversPanel, type AssignPanelBill } from "./AssignDriversPanel";

/** The open bill, as the strip reads it. */
export type BillDispatchBill = AssignPanelBill & {
  channel: InvoiceChannel;
  status: InvoiceStatus;
  aggregatorId: string | null;
  customerPhone: string | null;
  /** A typed rider's name, once a CLOSED bag left with one (T16). */
  handedTo: string | null;
};

/**
 * An own DELIVERY bill's dispatch state, on the invoice number's line (B6,
 * 2026-10-20, the user's layout): ONE badge, as tall as the buttons on that
 * line. Its tone and icon are the state — accepted green ✓, an offer amber
 * with the countdown, on the way blue — and its words the driver's name
 * (truncated; the full sentence with the action is its title and accessible
 * name, «قبِل ⟨name⟩ الطلب · تغيير المندوب»). For `DISPATCH_MANAGE` it is a
 * button: before pickup it opens the Delivery tab's own `AssignDriversPanel`
 * (withdraw inside); past pickup or with a problem open, the floor's
 * `DispatchProblemSheet`. A bag nobody holds (an offer declined, a delivery
 * returned) shows the state's short words, not a control. The tracking link
 * is not on the bill — it stays on the Delivery tab's row.
 *
 * The FIRST assign is the bill's primary foot button (`InvoicePage`'s
 * `dispatchFoot`, `BillAssignPanel` below), not part of this strip.
 */
export function BillDispatchStrip({
  bill,
  look,
}: {
  bill: BillDispatchBill;
  look: "paper" | "classic";
}) {
  const t = useTranslate();
  const chipText = useDispatchChipText();
  const access = useDispatchAccess();
  const ownDelivery =
    bill.channel === "DELIVERY" &&
    bill.aggregatorId === null &&
    (bill.status === "OPEN" || bill.status === "CLOSED");
  const dispatch = useDispatchBoard({ enabled: ownDelivery });
  const [open, setOpen] = useState<"assign" | "followUp" | null>(null);
  /* One id per retry key, kept across panel openings (D-10), as the tab's card does. */
  const [ids] = useState(() => new CommandIds());

  if (!ownDelivery || !access.canSeeBoard) return null;

  if (!dispatch.board) return null;

  const entry = dispatch.lookup(bill.invoiceId);
  const actions = dispatchActions(entry);
  const chip = dispatchChip(entry, dispatch.now, dispatch.skewMs);
  const followUp = actions.resolve || actions.end;

  const sheet =
    open === "assign" ? (
      <AssignDriversPanel
        bills={[bill]}
        lookup={dispatch.lookup}
        board={dispatch.board}
        ids={ids}
        onAssigned={() => undefined}
        onClose={() => setOpen(null)}
      />
    ) : open === "followUp" ? (
      <DispatchProblemSheet
        sequence={bill.dailySequence}
        entry={entry}
        onReassign={() => setOpen("assign")}
        onClose={() => setOpen(null)}
      />
    ) : null;

  if (!chip) return null;
  const held = access.canManage && (followUp || actions.reassign);
  const text = chipText(chip);
  const kind = actions.resolve ? "problem" : actions.end ? "followUp" : "change";
  const actionLabel =
    kind === "problem"
      ? t((m) => m.dispatch.actionProblem)
      : kind === "followUp"
        ? t((m) => m.dispatch.actionFollowUp)
        : t((m) => m.dispatch.actionChange);
  /* The user's tones (2026-10-20): accepted green, an offer amber, on the way
     blue, a problem red; the rest as the floor's chips. */
  const tone =
    chip.kind === "accepted" || chip.kind === "delivered"
      ? "success"
      : chip.kind === "offered"
        ? "warning"
        : chip.kind === "pickedUp" || chip.kind === "arrived"
          ? "info"
          : dispatchChipTone(chip.kind);
  const Icon =
    chip.kind === "accepted" || chip.kind === "delivered"
      ? BadgeCheck
      : chip.kind === "offered"
        ? Clock
        : chip.kind === "problem"
          ? TriangleAlert
          : chip.kind === "pickedUp" || chip.kind === "arrived" || chip.kind === "failed"
            ? Bike
            : Info;
  // A driver holds it: the badge says WHO (the state is its tone and icon).
  const holding = Boolean(entry?.current);
  const words = holding ? chip.driverName : text.short;
  // The countdown never truncates away with a long name.
  const countdown = holding && chip.kind === "offered" ? formatCountdown(chip.secondsLeft) : null;
  const name = held ? `${text.full} · ${actionLabel}` : text.full;
  const className = [
    "inline-flex min-w-0 max-w-[16rem] shrink items-center gap-1.5 border px-2.5 font-extrabold tabular-nums",
    look === "paper"
      ? "h-[30px] rounded-[6px] text-[length:var(--font-size-sm)]"
      : "h-8 rounded-[var(--radius-control)] text-[length:var(--font-size-2xs)]",
    BADGE_TONE[tone],
    held
      ? "cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--accent)]"
      : "",
  ].join(" ");
  const content = (
    <>
      <Icon aria-hidden="true" size={15} strokeWidth={2.2} className="shrink-0" />
      <bdi className="min-w-0 truncate">{words}</bdi>
      {countdown ? (
        <span data-ui="bill-dispatch-countdown" className="shrink-0" dir="ltr">
          · {countdown}
        </span>
      ) : null}
    </>
  );
  return (
    <span data-ui="bill-dispatch-head" data-look={look} data-testid="bill-dispatch-head" className="flex min-w-0 items-center">
      {held ? (
        <button
          type="button"
          data-ui="bill-dispatch-badge"
          data-tone={tone}
          data-kind={chip.kind}
          data-action={kind}
          data-testid="bill-dispatch-badge"
          aria-label={name}
          title={name}
          onClick={() => setOpen(followUp ? "followUp" : "assign")}
          className={className}
        >
          {content}
        </button>
      ) : (
        <span
          data-ui="bill-dispatch-badge"
          data-tone={tone}
          data-kind={chip.kind}
          data-testid="bill-dispatch-badge"
          role="status"
          aria-label={name}
          title={name}
          className={className}
        >
          {content}
        </span>
      )}
      {sheet}
    </span>
  );
}

/**
 * The bill's first «إسناد إلى مندوب» (the primary foot button): the Delivery
 * tab's own `AssignDriversPanel` on this one bag, over the same board read.
 * `ids` is the page's, so a lost answer is retried with the same command id
 * across openings (D-10).
 */
export function BillAssignPanel({
  bill,
  ids,
  onClose,
}: {
  bill: BillDispatchBill;
  ids: CommandIds;
  onClose: () => void;
}) {
  const dispatch = useDispatchBoard({ enabled: true });
  if (!dispatch.board) return null;
  return (
    <AssignDriversPanel
      bills={[bill]}
      lookup={dispatch.lookup}
      board={dispatch.board}
      ids={ids}
      onAssigned={() => undefined}
      onClose={onClose}
    />
  );
}
